import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, Store, ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";

/** Compact summary card shown at the top of the admin console showing
 * how many members/shops have completed the one-time first-login setup
 * (email verified + PIN set). Refreshes on mount. */
export default function SetupStatsCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/setup-stats");
        setData(data);
      } catch {
        setData(null);
      } finally { setBusy(false); }
    })();
  }, []);

  if (busy) return (
    <div data-testid="setup-stats-loading" className="rounded-xl border border-white/10 bg-[#170929] p-4 flex items-center gap-2 text-slate-400 text-sm">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading setup completion stats…
    </div>
  );
  if (!data) return null;

  const pct = (c, t) => (t ? Math.round((c / t) * 100) : 0);
  const mCompletedPct = pct(data.members.completed, data.members.total);
  const sCompletedPct = pct(data.shops.completed, data.shops.total);

  return (
    <div data-testid="setup-stats-card" className="rounded-xl border border-white/10 bg-[#170929] p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-[0.2em]">First-login setup completion</div>
          <div className="text-white text-sm mt-1">Members and shops that have verified email + set a 4-digit PIN.</div>
        </div>
        <ShieldCheck className="w-6 h-6 text-pink-400" strokeWidth={1.5} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Members */}
        <div data-testid="setup-stats-members" className="rounded-lg border border-white/10 bg-[#150733] p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-slate-300">
              <Users className="w-4 h-4 text-pink-400" strokeWidth={2} />
              <span className="text-sm font-medium">Members</span>
            </div>
            <span className="text-xs text-slate-500">{mCompletedPct}% complete</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-md bg-white/5 p-2">
              <div data-testid="stats-members-total" className="text-2xl text-white font-light">{data.members.total}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Total</div>
            </div>
            <div className="rounded-md bg-emerald-500/10 border border-emerald-500/20 p-2">
              <div data-testid="stats-members-completed" className="text-2xl text-emerald-300 font-light">{data.members.completed}</div>
              <div className="text-[10px] text-emerald-400/70 uppercase tracking-wider mt-0.5">Completed</div>
            </div>
            <div className="rounded-md bg-amber-500/10 border border-amber-500/20 p-2">
              <div data-testid="stats-members-pending" className="text-2xl text-amber-300 font-light">{data.members.pending}</div>
              <div className="text-[10px] text-amber-400/70 uppercase tracking-wider mt-0.5">Pending</div>
            </div>
          </div>
          <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full bg-emerald-500/70" style={{ width: `${mCompletedPct}%` }} />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3 text-[11px] text-slate-500">
            <div>Customers: <span className="text-slate-300">{data.members.customers.completed}</span> / {data.members.customers.total}</div>
            <div>Affiliates: <span className="text-slate-300">{data.members.affiliates.completed}</span> / {data.members.affiliates.total}</div>
          </div>
        </div>

        {/* Shops */}
        <div data-testid="setup-stats-shops" className="rounded-lg border border-white/10 bg-[#150733] p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-slate-300">
              <Store className="w-4 h-4 text-pink-400" strokeWidth={2} />
              <span className="text-sm font-medium">Tie-up shops</span>
            </div>
            <span className="text-xs text-slate-500">{sCompletedPct}% complete</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-md bg-white/5 p-2">
              <div data-testid="stats-shops-total" className="text-2xl text-white font-light">{data.shops.total}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Total</div>
            </div>
            <div className="rounded-md bg-emerald-500/10 border border-emerald-500/20 p-2">
              <div data-testid="stats-shops-completed" className="text-2xl text-emerald-300 font-light">{data.shops.completed}</div>
              <div className="text-[10px] text-emerald-400/70 uppercase tracking-wider mt-0.5">Completed</div>
            </div>
            <div className="rounded-md bg-amber-500/10 border border-amber-500/20 p-2">
              <div data-testid="stats-shops-pending" className="text-2xl text-amber-300 font-light">{data.shops.pending}</div>
              <div className="text-[10px] text-amber-400/70 uppercase tracking-wider mt-0.5">Pending</div>
            </div>
          </div>
          <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full bg-emerald-500/70" style={{ width: `${sCompletedPct}%` }} />
          </div>
          {data.shops.pending > 0 && (
            <div className="mt-3 text-[11px] text-amber-400/80 flex items-center gap-1.5">
              <ShieldAlert className="w-3 h-3" strokeWidth={2} />
              {data.shops.pending} shop{data.shops.pending === 1 ? "" : "s"} still on first-login setup
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
